/**
 * DataTableMUI — wrapper mỏng quanh MUI X DataGrid với theme N2NAI.
 * Mọi page dùng grid đều đi qua đây để chuẩn hoá look & feel.
 *
 * Pass `storageKey` để persist width per cột vào backend user_preferences
 * (page_path='datatable', preference_key='column_widths'). Bỏ trống storageKey
 * thì resize vẫn chạy nhưng không lưu.
 */
import { useCallback, useMemo } from 'react'
import { Box } from '@mui/material'
import {
  DataGrid,
  type DataGridProps,
  type GridColDef,
  type GridColumnResizeParams,
  type GridRowsProp,
} from '@mui/x-data-grid'

import { useColumnWidths } from '@/hooks/useColumnWidths'

import { futuresAccessDataGridSx } from '../pages/futures/futuresAccessUi'

export interface DataTableMUIProps<R extends Record<string, unknown> = Record<string, unknown>>
  extends Omit<DataGridProps, 'rows' | 'columns'> {
  rows: GridRowsProp<R>
  columns: GridColDef<R>[]
  height?: number | string
  /** Kiểu datasheet / Access Futures — viền ô, header xám. */
  accessDatasheet?: boolean
  /**
   * Bật persist width per cột qua backend user_preferences.
   * Mỗi table cần một key duy nhất (vd "factory.bom.items").
   * Bỏ trống = vẫn resize được nhưng không lưu.
   */
  storageKey?: string
}

export function DataTableMUI<R extends Record<string, unknown> = Record<string, unknown>>({
  rows,
  columns,
  height,
  accessDatasheet,
  storageKey,
  onColumnWidthChange,
  sx,
  ...rest
}: DataTableMUIProps<R>) {
  const { getWidth, setWidth } = useColumnWidths(storageKey)

  const mergedColumns = useMemo(() => {
    if (!storageKey) return columns
    return columns.map((col) => {
      const w = getWidth(col.field)
      return w != null ? { ...col, width: w } : col
    })
  }, [columns, getWidth, storageKey])

  const handleColumnWidthChange = useCallback<NonNullable<DataGridProps['onColumnWidthChange']>>(
    (params: GridColumnResizeParams, event, details) => {
      if (storageKey) setWidth(params.colDef.field, params.width)
      onColumnWidthChange?.(params, event, details)
    },
    [storageKey, setWidth, onColumnWidthChange],
  )

  return (
    <Box
      sx={{
        width: '100%',
        // Default: sm+ keeps fixed 560; xs uses a viewport-relative clamp so the
        // grid can shrink on small phones instead of forcing whole-page scroll.
        // An explicit `height` prop from a consumer still wins.
        height: height ?? { xs: 'clamp(360px, calc(100dvh - 220px), 560px)', sm: 560 },
      }}
    >
      <DataGrid
        rows={rows}
        columns={mergedColumns as GridColDef[]}
        disableRowSelectionOnClick
        density="compact"
        pageSizeOptions={[10, 25, 50, 100]}
        onColumnWidthChange={handleColumnWidthChange}
        sx={{
          backgroundColor: (t) => t.palette.background.paper,
          borderRadius: 1,
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: (t) => t.palette.action.hover,
          },
          ...(accessDatasheet ? futuresAccessDataGridSx : {}),
          ...(sx as object),
        }}
        {...rest}
      />
    </Box>
  )
}

export default DataTableMUI
